import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Download, ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal } from "@/components/ui-custom/Reveal";
import { resolveUrl, pdfHref } from "@/lib/api";
import { EASE } from "@/lib/motion";

const ROTATE_MS = 6000;

export const NewBrandsSlider = ({ brands = [], label = "New Product" }) => {
  const items = useMemo(() => brands.filter((b) => b && b.name).slice(0, 8), [brands]);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const count = items.length;

  const go = useCallback((n) => setIndex((i) => (n + count) % count), [count]);

  useEffect(() => { if (index >= count) setIndex(0); }, [count, index]);

  useEffect(() => {
    if (count <= 1 || paused) return;
    const t = setInterval(() => setIndex((i) => (i + 1) % count), ROTATE_MS);
    return () => clearInterval(t);
  }, [count, paused]);

  if (count === 0) return null;
  const brand = items[Math.min(index, count - 1)];
  const pdf = pdfHref(brand);

  return (
    <section className="bg-[var(--cream)] py-14 lg:py-20" data-testid="new-brands-slider">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
        <Reveal>
          <div className="flex items-end justify-between mb-6">
            <div>
              <p className="eyebrow text-[var(--terracotta)] mb-2">Just landed</p>
              <h2 className="font-display text-2xl sm:text-3xl text-[var(--ink)]">New to the Deliver range</h2>
            </div>
            {count > 1 && (
              <div className="flex items-center gap-2" data-testid="new-brands-controls">
                <button onClick={() => go(index - 1)} aria-label="Previous brand" data-testid="new-brands-prev" className="h-10 w-10 grid place-items-center rounded-full border border-[var(--sand)] text-[var(--ink)] hover:border-[var(--terracotta)] hover:text-[var(--terracotta)] transition-colors"><ChevronLeft size={18} /></button>
                <button onClick={() => go(index + 1)} aria-label="Next brand" data-testid="new-brands-next" className="h-10 w-10 grid place-items-center rounded-full border border-[var(--sand)] text-[var(--ink)] hover:border-[var(--terracotta)] hover:text-[var(--terracotta)] transition-colors"><ChevronRight size={18} /></button>
              </div>
            )}
          </div>

          <div
            className="relative border border-[var(--sand)] rounded-sm overflow-hidden bg-[var(--cream-2)]"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={brand.slug || index}
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.6, ease: EASE }}
                className="grid grid-cols-1 md:grid-cols-12"
                data-testid={`new-brand-slide-${brand.slug}`}
              >
                <div className="md:col-span-5 lg:col-span-4 relative min-h-[240px]">
                  <img src={resolveUrl(brand.hero_image_url)} alt={brand.image_alt || brand.name} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                  <span className="absolute top-4 left-4 inline-flex items-center gap-2 bg-[var(--terracotta)] text-[var(--cream)] eyebrow !text-[0.62rem] px-3 py-1.5 rounded-full"><span className="h-1.5 w-1.5 rounded-full bg-[var(--cream)] animate-pulse" /> {label}</span>
                </div>
                <div className="md:col-span-7 lg:col-span-8 p-8 lg:p-12 flex flex-col lg:flex-row lg:items-center gap-8">
                  <div className="flex-1">
                    <div className="flex items-center gap-4">
                      {brand.logo_url && <div className="bg-[var(--cream)] rounded-sm p-1.5 h-14 w-14 shrink-0"><img src={resolveUrl(brand.logo_url)} alt="" className="h-full w-full object-contain" /></div>}
                      <h3 className="font-display font-normal text-2xl sm:text-3xl lg:text-4xl leading-tight text-[var(--ink)]" data-testid="new-brand-title">{brand.name}</h3>
                    </div>
                    <p className="mt-4 text-base text-[var(--charcoal)] font-light leading-relaxed max-w-2xl">{brand.tagline ? brand.tagline + ". " : ""}{(brand.description || "").split(". ")[0]}.</p>
                  </div>
                  <div className="flex flex-wrap lg:flex-col gap-3 shrink-0">
                    <Link to={`/brands/${brand.slug}`} className="btn-pill btn-terracotta !py-3 !px-5" data-testid="new-brand-cta">Explore {brand.name.split(" ")[0]} <ArrowUpRight size={14} /></Link>
                    {pdf && <a href={pdf} target="_blank" rel="noreferrer" className="btn-pill btn-outline !py-3 !px-5" data-testid="new-brand-pdf">View full range <Download size={14} /></a>}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {count > 1 && (
            <div className="flex items-center justify-center gap-2 mt-5" data-testid="new-brands-dots">
              {items.map((b, i) => (
                <button
                  key={b.slug || i}
                  onClick={() => setIndex(i)}
                  aria-label={`Show ${b.name}`}
                  data-testid={`new-brands-dot-${i}`}
                  className={`h-2 rounded-full transition-all duration-300 ${i === index ? "w-6 bg-[var(--terracotta)]" : "w-2 bg-[var(--sand)] hover:bg-[var(--stone)]"}`}
                />
              ))}
            </div>
          )}
        </Reveal>
      </div>
    </section>
  );
};
